import React, { useState, useEffect } from 'react';
import reviewService from '../../services/reviewService';
import { reviewService as apiReviewService } from '../../services/api';
import useProducts from '../hooks/useProducts';
import './CustomerReviewsModal.css';

const getDefaultDateTime = () => {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
};

const CustomerReviewsModal = ({ product, onClose, onReviewsUpdated, canEditReviews = true }) => {
  const { uploadImage } = useProducts();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('list'); // 'list' | 'add'
  const [editingReview, setEditingReview] = useState(null);
  const [formData, setFormData] = useState({
    customerName: '',
    rating: 5,
    title: '',
    comment: '',
    imageFile: null,
    imagePreview: null,
    reviewDate: getDefaultDateTime()
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const productId = product?._id || product?.id;
  const token = localStorage.getItem('adminToken') || localStorage.getItem('authToken');

  useEffect(() => {
    if (productId) loadReviews();
  }, [productId]);

  useEffect(() => {
    if (!canEditReviews) {
      setActiveTab('list');
      setEditingReview(null);
    }
  }, [canEditReviews]);

  const loadReviews = async () => {
    try {
      setLoading(true);
      const res = await reviewService.getProductReviews(productId, { limit: 50, sort: 'newest' });
      setReviews(res.data?.reviews || res.reviews || res.data || []);
    } catch (err) {
      console.error('Error loading reviews:', err);
      setReviews([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file (JPG, PNG, etc.)');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setError('Image must be less than 5MB');
        return;
      }
      setFormData(prev => ({
        ...prev,
        imageFile: file,
        imagePreview: URL.createObjectURL(file)
      }));
      setError('');
    }
  };

  const removeImage = () => {
    setFormData(prev => {
      if (prev.imagePreview) URL.revokeObjectURL(prev.imagePreview);
      return { ...prev, imageFile: null, imagePreview: null };
    });
  };

  const validateForm = () => {
    if (!editingReview && !formData.customerName.trim()) {
      setError('Customer name is required');
      return false;
    }
    if (!formData.title.trim()) {
      setError('Review title is required');
      return false;
    }
    if (!formData.comment.trim()) {
      setError('Review comment is required');
      return false;
    }
    if (formData.comment.length < 10) {
      setError('Comment must be at least 10 characters');
      return false;
    }
    if (formData.rating < 1 || formData.rating > 5) {
      setError('Please select a rating (1-5 stars)');
      return false;
    }
    return true;
  };

  const handleSubmitAdd = async (e) => {
    e.preventDefault();
    if (!validateForm() || !token) return;

    setSubmitting(true);
    setError('');

    try {
      let imageUrl = null;
      if (formData.imageFile && uploadImage) {
        const uploadResult = await uploadImage(formData.imageFile);
        if (uploadResult?.url) imageUrl = uploadResult.url;
      }

      const createdAt = formData.reviewDate ? new Date(formData.reviewDate).toISOString() : undefined;
      const reviewPayload = {
        customerName: formData.customerName.trim(),
        rating: formData.rating,
        title: formData.title.trim(),
        comment: formData.comment.trim(),
        ...(imageUrl && { images: [{ url: imageUrl }] }),
        ...(createdAt && { createdAt })
      };

      try {
        await apiReviewService.createAdminReview(productId, reviewPayload);
      } catch (adminErr) {
        const fallbackPayload = {
          product: productId,
          ...reviewPayload
        };
        await reviewService.createReview(fallbackPayload, token);
      }

      resetForm();
      await loadReviews();
      setActiveTab('list');
      onReviewsUpdated?.();
    } catch (err) {
      setError(err.message || 'Failed to add review');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitEdit = async (e) => {
    e.preventDefault();
    if (!editingReview || !validateForm() || !token) return;

    setSubmitting(true);
    setError('');

    try {
      let imageUrl = null;
      if (formData.imageFile && uploadImage) {
        const uploadResult = await uploadImage(formData.imageFile);
        if (uploadResult?.url) imageUrl = uploadResult.url;
      }

      const updatePayload = {
        rating: formData.rating,
        title: formData.title.trim(),
        comment: formData.comment.trim(),
        ...(imageUrl && { images: [{ url: imageUrl }] })
      };

      await reviewService.updateReview(editingReview._id, updatePayload, token);
      resetForm();
      setEditingReview(null);
      await loadReviews();
      setActiveTab('list');
      onReviewsUpdated?.();
    } catch (err) {
      setError(err.message || 'Failed to update review');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (reviewId) => {
    if (!window.confirm('Delete this review?')) return;
    try {
      await reviewService.deleteReview(reviewId, token);
      await loadReviews();
      onReviewsUpdated?.();
    } catch (err) {
      alert(err.message || 'Failed to delete review');
    }
  };

  const startEdit = (review) => {
    setEditingReview(review);
    setFormData({
      customerName: review.user?.name || review.customerName || '',
      rating: review.rating || 5,
      title: review.title || '',
      comment: review.comment || '',
      imageFile: null,
      imagePreview: null
    });
    setActiveTab('add');
  };

  const resetForm = () => {
    setFormData({
      customerName: '',
      rating: 5,
      title: '',
      comment: '',
      imageFile: null,
      imagePreview: null,
      reviewDate: getDefaultDateTime()
    });
    setEditingReview(null);
    setError('');
  };

  const renderStars = (rating, editable = false, onChange) => {
    return Array.from({ length: 5 }, (_, i) => {
      const cls = `star-btn ${i < rating ? 'active' : ''}`;
      return editable ? (
        <button key={i} type="button" className={cls} onClick={() => onChange?.(i + 1)}>
          <i className="fas fa-star"></i>
        </button>
      ) : (
        <span key={i} className={cls}><i className="fas fa-star"></i></span>
      );
    });
  };

  const formatDate = (d) => {
    if (!d) return '';
    return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  if (!product) return null;

  return (
    <div className="customer-reviews-modal-overlay" onClick={onClose}>
      <div className="customer-reviews-modal" onClick={e => e.stopPropagation()}>
        <div className="customer-reviews-modal-header">
          <h2>
            <i className="fas fa-star" aria-hidden />
            Customer Reviews — {product.name}
          </h2>
          <button
            type="button"
            className="customer-reviews-modal-close"
            onClick={onClose}
            aria-label="Close reviews"
          >
            <i className="fas fa-times" aria-hidden />
          </button>
        </div>

        {canEditReviews ? (
          <div className="customer-reviews-modal-tabs">
            <button
              type="button"
              className={`tab-btn ${activeTab === 'list' ? 'active' : ''}`}
              onClick={() => { setActiveTab('list'); resetForm(); }}
            >
              <i className="fas fa-list" aria-hidden /> View &amp; Edit ({reviews.length})
            </button>
            <button
              type="button"
              className={`tab-btn ${activeTab === 'add' ? 'active' : ''}`}
              onClick={() => { setActiveTab('add'); setEditingReview(null); resetForm(); }}
            >
              <i className="fas fa-plus" aria-hidden /> Add Review
            </button>
          </div>
        ) : null}

        <div className="customer-reviews-modal-body">
          {activeTab === 'list' && (
            <div className="reviews-list-section">
              {loading ? (
                <div className="reviews-loading"><i className="fas fa-spinner fa-spin"></i> Loading reviews...</div>
              ) : reviews.length === 0 ? (
                <div className="reviews-empty">
                  <i className="fas fa-comments" aria-hidden />
                  <p>No reviews yet for this product.</p>
                  {canEditReviews ? (
                    <button type="button" className="btn-add-first" onClick={() => setActiveTab('add')}>
                      Add First Review
                    </button>
                  ) : null}
                </div>
              ) : (
                <div className="reviews-list">
                  {reviews.map(review => (
                    <div key={review._id} className="review-item">
                      <div className="review-item-header">
                        <div className="review-item-meta">
                          <span className="reviewer-name">{review.user?.name || review.customerName || 'Customer'}</span>
                          <div className="review-item-stars">
                            {renderStars(review.rating)}
                          </div>
                          <span className="review-date">{formatDate(review.createdAt)}</span>
                        </div>
                        {canEditReviews ? (
                          <div className="review-item-actions">
                            <button
                              type="button"
                              className="btn-edit"
                              onClick={() => startEdit(review)}
                              title="Edit"
                            >
                              <i className="fas fa-edit" aria-hidden />
                            </button>
                            <button
                              type="button"
                              className="btn-delete"
                              onClick={() => handleDelete(review._id)}
                              title="Delete"
                            >
                              <i className="fas fa-trash" aria-hidden />
                            </button>
                          </div>
                        ) : null}
                      </div>
                      <h4 className="review-item-title">{review.title}</h4>
                      <p className="review-item-comment">{review.comment}</p>
                      {review.images?.length > 0 && (
                        <div className="review-item-images">
                          {review.images.map((img, i) => (
                            <img key={i} src={img.url || img} alt={`Review ${i + 1}`} />
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {canEditReviews && activeTab === 'add' && (
            <form
              className="add-review-form"
              onSubmit={editingReview ? handleSubmitEdit : handleSubmitAdd}
            >
              <h3>{editingReview ? 'Edit Review' : 'Add Customer Review'}</h3>

              {!editingReview && (
                <div className="form-group">
                  <label>Customer Name *</label>
                  <input
                    type="text"
                    value={formData.customerName}
                    onChange={e => handleInputChange('customerName', e.target.value)}
                    placeholder="Enter customer name"
                    maxLength={100}
                  />
                </div>
              )}

              {!editingReview && (
                <div className="form-group">
                  <label>Review Date & Time</label>
                  <input
                    type="datetime-local"
                    value={formData.reviewDate}
                    onChange={e => handleInputChange('reviewDate', e.target.value)}
                    className="datetime-input"
                  />
                </div>
              )}

              <div className="form-group">
                <label>Rating (1-5 stars) *</label>
                <div className="rating-stars-input">
                  {renderStars(formData.rating, true, (r) => handleInputChange('rating', r))}
                  <span className="rating-label">
                    {formData.rating === 1 && 'Poor'}
                    {formData.rating === 2 && 'Fair'}
                    {formData.rating === 3 && 'Good'}
                    {formData.rating === 4 && 'Very Good'}
                    {formData.rating === 5 && 'Excellent'}
                  </span>
                </div>
              </div>

              <div className="form-group">
                <label>Review Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={e => handleInputChange('title', e.target.value)}
                  placeholder="Summarize the review"
                  maxLength={100}
                />
              </div>

              <div className="form-group">
                <label>Review Comment *</label>
                <textarea
                  value={formData.comment}
                  onChange={e => handleInputChange('comment', e.target.value)}
                  placeholder="Write the review content..."
                  rows={4}
                  maxLength={1000}
                />
              </div>

              <div className="form-group">
                <label>Review Image (optional)</label>
                <div className="image-upload-area">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    id="review-image-upload"
                    className="hidden-input"
                  />
                  <label htmlFor="review-image-upload" className="upload-label">
                    <i className="fas fa-cloud-upload-alt"></i>
                    <span>Click to upload image</span>
                  </label>
                  {formData.imagePreview && (
                    <div className="image-preview-wrap">
                      <img src={formData.imagePreview} alt="Preview" />
                      <button type="button" className="remove-image-btn" onClick={removeImage}>
                        <i className="fas fa-times"></i>
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {error && <div className="form-error">{error}</div>}

              <div className="form-actions">
                <button type="button" className="btn-cancel" onClick={() => { resetForm(); setActiveTab('list'); }}>
                  Cancel
                </button>
                <button type="submit" className="btn-submit" disabled={submitting}>
                  {submitting ? <><i className="fas fa-spinner fa-spin"></i> Saving...</> : (editingReview ? 'Update Review' : 'Add Review')}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerReviewsModal;
